import { Auth0ContextInterface } from "@auth0/auth0-react";
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import {
  ViCorePostCaseResponse,
  ViCorePostImageResponse,
  ViCoreGetProcessingResponse,
  ViCorePostCaseRequest,
  ViImageData,
  ViCoreOrigin,
} from "../models";
import {
  ViEnvVariableNotSetError,
  ViError,
  ViUserNotAuthenticatedError,
} from "../models/Errors";

class Client {
  config: {
    origin: ViCoreOrigin;
    carModel: { manufacturerName: string; modelName: string };
  };
  userId?: string;
  AuthContext?: Auth0ContextInterface;
  axios?: AxiosInstance;
  baseURL?: string = process.env.REACT_APP_VICORE_BASE_URL;

  constructor() {
    this.config = {
      origin: {
        system: process.env.REACT_APP_ORIGIN_SYSTEM || "vi2-demo-ui",
        caseId: process.env.REACT_APP_ORIGIN_CASE_ID || "vi2-demo-ui",
        customerId: process.env.REACT_APP_ORIGIN_CUSTOMER_ID || "vi2-demo-ui",
        country: process.env.REACT_APP_ORIGIN_COUNTRY || "US",
      },
      carModel: {
        manufacturerName: process.env.REACT_APP_MANUFACTURER_NAME || "vi2-demo-ui",
        modelName: process.env.REACT_APP_MODEL_NAME || "vi2-demo-ui",
      },
    };
  }

  init(AuthContext: Auth0ContextInterface): Client {
    if (!AuthContext.isAuthenticated) throw new ViUserNotAuthenticatedError();
    if (!this.baseURL) throw new ViEnvVariableNotSetError("REACT_APP_VICORE_BASE_URL");
    this.config.origin.customerId =
      AuthContext.user?.email || AuthContext.user?.nickname || "unkown user";
    this.AuthContext = AuthContext;
    this.axios = axios.create({
      baseURL: this.baseURL,
    });
    return this;
  }

  async req({ headers, ...config }: AxiosRequestConfig): Promise<AxiosResponse<any>> {
    if (!this.axios) throw new ViError("ViCoreClient has not been initialized.");
    try {
      headers = {
        ...headers,
        Authorization: `Bearer ${await this.AuthContext?.getAccessTokenSilently()}`,
      };
      const response = this.axios?.request({ headers, ...config });
      return response;
    } catch (e) {
      throw e;
    }
  }

  async createCase(): Promise<ViCorePostCaseResponse> {
    const response = await this.req({
      method: "post",
      url: "v2/cases",
      data: new ViCorePostCaseRequest(this.config.origin, this.config.carModel),
    });
    return response.data;
  }

  async uploadImages(
    caseId: string,
    imageData: ViImageData[]
  ): Promise<ViCorePostImageResponse> {
    const response = await this.req({
      method: "post",
      url: `v3/cases/${caseId}/images`,
      data: await buildFormData(imageData),
      headers: {
        "Content-Type": "multipart/form-data; boundary=2",
      },
    });
    return response.data;
  }

  deleteImage(caseId: string, imageId: string): Promise<AxiosResponse<any>> {
    return this.req({
      method: "delete",
      url: `v2/cases/${caseId}/images/${imageId}`,
    });
  }

  async getProcessing(
    caseId: string,
    threshold: number = 0.4
  ): Promise<ViCoreGetProcessingResponse> {
    const response = await this.req({
      method: "get",
      url: `v2/cases/${caseId}/processing`,
      params: {
        optionalProperties: "contour",
        experimental: true,
        totalLoss: true,
        aggregatedPartDamagesThreshold: threshold,
      },
    });
    return response.data;
  }
}

async function buildFormData(imageData: ViImageData[]): Promise<FormData> {
  const formData: FormData = new FormData();
  const promises = await imageData.map(async ({ data, filename }) => {
    const res = await fetch(data);
    const blob = await res.blob();
    const file = new File([blob], filename);
    return file;
  });
  const files = await Promise.all(promises);
  files.forEach((file) => {
    formData.append("images", file);
  });
  return formData;
}

export const ViCoreClient = new Client();
